import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { BarChart3, MessageSquare, TrendingUp, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface Analytics {
  totalAIResponses: number;
  aiResponseRate: number;
  avgTurnsBeforeEscalation: number;
  activeKnowledgeEntries: number;
}

interface AIConversation {
  id: string;
  customer_phone: string;
  customer_name: string | null;
  ai_turn_count: number;
  escalation_requested: boolean;
  last_message_at: string;
}

export function AIAnalytics() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<Analytics>({
    totalAIResponses: 0,
    aiResponseRate: 0,
    avgTurnsBeforeEscalation: 0,
    activeKnowledgeEntries: 0
  });
  const [recentConversations, setRecentConversations] = useState<AIConversation[]>([]);
  const [unansweredQuestions, setUnansweredQuestions] = useState<any[]>([]);

  useEffect(() => {
    fetchAnalytics();

    const channel = supabase
      .channel('analytics-channel')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages'
        },
        () => {
          fetchAnalytics();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Total AI responses
      const { count: aiResponseCount } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('sender_type', 'ai');

      // Total messages
      const { count: totalMessages } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true });

      // AI response rate
      const aiResponseRate = totalMessages ? ((aiResponseCount || 0) / totalMessages) * 100 : 0;

      // Average turns before escalation
      const { data: escalatedConvos } = await supabase
        .from('conversations')
        .select('ai_turn_count')
        .eq('escalation_requested', true)
        .gt('ai_turn_count', 0);

      const avgTurns = escalatedConvos && escalatedConvos.length > 0
        ? escalatedConvos.reduce((sum, c) => sum + c.ai_turn_count, 0) / escalatedConvos.length
        : 0;

      // Active knowledge entries
      const { count: activeEntries } = await supabase
        .from('knowledge_base')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      setAnalytics({
        totalAIResponses: aiResponseCount || 0,
        aiResponseRate: Math.round(aiResponseRate),
        avgTurnsBeforeEscalation: Math.round(avgTurns * 10) / 10,
        activeKnowledgeEntries: activeEntries || 0
      });

      // Recent AI conversations
      const { data: recentAI } = await supabase
        .from('conversations')
        .select('*')
        .eq('ai_handled', true)
        .order('last_message_at', { ascending: false })
        .limit(20);

      setRecentConversations(recentAI || []);

      // Unanswered questions (escalated conversations)
      const { data: unanswered } = await supabase
        .from('conversations')
        .select('*, messages!inner(*)')
        .eq('escalation_requested', true)
        .gt('ai_turn_count', 0)
        .order('last_message_at', { ascending: false })
        .limit(10);

      setUnansweredQuestions(unanswered || []);

    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load analytics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total AI Responses</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.totalAIResponses}</div>
            <p className="text-xs text-muted-foreground">Messages sent by AI</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">AI Response Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.aiResponseRate}%</div>
            <p className="text-xs text-muted-foreground">Of all messages</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Turns to Escalation</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.avgTurnsBeforeEscalation}</div>
            <p className="text-xs text-muted-foreground">AI responses before human</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Knowledge Entries</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics.activeKnowledgeEntries}</div>
            <p className="text-xs text-muted-foreground">Active entries</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent AI Conversations</CardTitle>
          <CardDescription>Last 20 conversations handled by AI</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>AI Turns</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Message</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recentConversations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No AI conversations yet
                  </TableCell>
                </TableRow>
              ) : (
                recentConversations.map((conv) => (
                  <TableRow key={conv.id}>
                    <TableCell>
                      {conv.customer_name || conv.customer_phone}
                    </TableCell>
                    <TableCell>{conv.ai_turn_count}</TableCell>
                    <TableCell>
                      {conv.escalation_requested ? (
                        <Badge variant="outline">Escalated</Badge>
                      ) : (
                        <Badge>AI Handled</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(conv.last_message_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate('/dashboard')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Escalated Conversations</CardTitle>
          <CardDescription>Questions that needed human assistance</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Last Message</TableHead>
                <TableHead>AI Turns</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {unansweredQuestions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No escalated conversations
                  </TableCell>
                </TableRow>
              ) : (
                unansweredQuestions.map((conv) => {
                  const lastCustomerMsg = conv.messages
                    ?.filter((m: any) => m.sender_type === 'customer')
                    .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];

                  return (
                    <TableRow key={conv.id}>
                      <TableCell>{conv.customer_name || conv.customer_phone}</TableCell>
                      <TableCell className="max-w-xs truncate">
                        {lastCustomerMsg?.content || 'N/A'}
                      </TableCell>
                      <TableCell>{conv.ai_turn_count}</TableCell>
                      <TableCell>
                        {new Date(conv.last_message_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
